import BaseFontIcon from '@/components/ui/BaseFontIcon';
import BaseIconButton from '@/components/ui/BaseIconButton';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface ScannerControlsProps {
    scannerEnabled: boolean;
    onEnableScanner: () => void;

    cameraEnabled: boolean;
    onEnableCamera: () => void | Promise<void>;

    cameras: { deviceId: string; label: string }[];
    cameraLoading?: boolean;
    cameraLoaded?: boolean;

    selectedCamera: string;
    onSelectCamera: (cameraId: string) => void;
}

const getScannerButtonSx = (active: boolean) => ({
    width: 30,
    height: 30,
    minWidth: 30,
    p: 0.5,
    border: 'none',
    borderRadius: '6px',
    bgcolor: active ? 'primary.main' : 'action.disabledBackground',
    color: active ? '#fff' : 'text.secondary',
    WebkitTapHighlightColor: 'transparent',

    '&.Mui-disabled': {
        opacity: 1,
        bgcolor: active ? 'primary.main' : 'action.disabledBackground',
        color: active ? '#fff' : 'text.disabled',
    },

    '&:focus, &:focus-visible': {
        outline: 'none',
    },

    '& i, & svg': {
        fontSize: 18,
        color: 'inherit',
    },
});

const ScannerControls: FC<ScannerControlsProps> = ({
    scannerEnabled,
    onEnableScanner,
    cameraEnabled,
    onEnableCamera,
    cameras,
    cameraLoading = false,
    cameraLoaded = false,
    selectedCamera,
    onSelectCamera,
}) => {
    const { t } = useTranslation('common');

    const shouldShowCameraLoading = cameraEnabled && cameraLoading;
    const shouldShowCameraSelect = cameraEnabled && cameraLoaded && cameras.length > 0;
    // const shouldShowNoCamera = cameraEnabled && cameraLoaded && cameras.length === 0;

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mt: 2.5,
                gap: 1,
                WebkitTapHighlightColor: 'transparent',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                }}
            >
                <BaseIconButton
                    type="button"
                    disabled={scannerEnabled}
                    aria-label={t('Hardware scanner')}
                    onClick={() => {
                        if (scannerEnabled) return;
                        onEnableScanner();
                    }}
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                    sx={getScannerButtonSx(scannerEnabled)}
                >
                    <BaseFontIcon className="fa-regular fa-barcode-read" />
                </BaseIconButton>

                <BaseIconButton
                    type="button"
                    disabled={cameraEnabled || cameraLoading}
                    aria-label={t('Camera scanner')}
                    onClick={() => {
                        if (cameraEnabled || cameraLoading) return;
                        void onEnableCamera();
                    }}
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                    sx={getScannerButtonSx(cameraEnabled)}
                >
                    <BaseFontIcon className="fa-regular fa-camera-viewfinder" />
                </BaseIconButton>
                {shouldShowCameraLoading && (
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 700,
                            color: 'text.secondary',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {t('Loading cameras...')}
                    </Typography>
                )}

                {/* {shouldShowNoCamera && (
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 700,
                            color: 'error.main',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {t('No camera found')}
                    </Typography>
                )} */}
                {shouldShowCameraSelect && (
                    <Select
                        size="small"
                        displayEmpty
                        value={selectedCamera}
                        onChange={(event) => onSelectCamera(event.target.value)}
                        sx={{
                            flex: '1 1 160px',
                            height: 30,
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            bgcolor: 'background.paper',

                            '& .MuiSelect-select': {
                                py: 0.75,
                            },

                            '& fieldset': {
                                borderColor: 'divider',
                            },

                            '&:focus, &:focus-visible': {
                                outline: 'none',
                            },
                        }}
                    >
                        <MenuItem value="">{t('Default camera')}</MenuItem>
                        {cameras.map((camera) => (
                            <MenuItem key={camera.deviceId} value={camera.deviceId}>
                                {camera.label}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            </Box>
        </Box>
    );
};

export default ScannerControls;
